import {IProductListingItem} from "../../interfaces/IProductListingItem";
import ProductCard from "./ProductCard";
import styled from "styled-components";
import React, {useContext, useEffect, useState} from "react";
import Icon from "../Icon";
import baseTheme from "../../shared/baseTheme";
import {Col, Row} from "react-bootstrap";
import {getLocalStorageItem, setLocalStorageItem} from "../../shared/localStorageUtil";
import Select from 'react-select'
import {IListing} from "../../interfaces/IListing";
import {ISelectType} from "../../interfaces/ISelectType";
import {ListingSortingContext} from "../../contexts/ListingSortingContext";
import {useRouter} from "next/router";
import {IClickSource} from "../../interfaces/IClickSource";
import {useMessages} from "../../hooks/useMessages";
import Link from "next/link";
import {useCountryLang} from "../../hooks/useCountryLang";
import {getTheme} from "../../shared/themeUtils";

const ListingContain = styled.div`
  margin-top: ${getTheme("space.3")};
  border-top: 1px solid ${getTheme("colors.border")};
  width: 100%;

  @media (max-width: ${getTheme("breakpoints.0")}) {
    margin-top: ${getTheme("space.2")};
  }
  
  .grid-col {
    padding: 0 ${getTheme("space.1")};
  }

  .compare-top-prods {
    .link-content {
      color: ${getTheme("colors.link")};
      display: flex;
        align-items: center;
      .compare-icon {
        margin-right: ${getTheme("space.1")};
      }

        @media (max-width: ${getTheme("breakpoints.1")}) {
            margin: ${getTheme("space.0")};
        }
    }
  }
`;

const ListingControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
    flex-wrap: wrap;
  padding-top: ${getTheme("space.2")};
  padding-bottom: ${getTheme("space.1")};
  .top-compare-link {
    margin-left: ${getTheme("space.1")};
    margin-right: auto;
  }
`;

const ListingView = styled.div`
  display: flex;

  .layout-icon {
    cursor: pointer;
    margin-right: ${getTheme("space.1")};
  }

    @media (max-width: ${getTheme("breakpoints.1")}) {
        display: none;
    }
`;

const SortingOptions = styled.div`
  width: ${getTheme("space.6")};

    @media (max-width: ${getTheme("breakpoints.1")}) {
        margin: ${getTheme("space.0")} 0;
    }
`

enum ListingType {
  LIST = "list",
  GRID = "grid"
}

interface ProductListingProps {
  pages?: IListing[],
  initialData?: IProductListingItem[]
  products?: IProductListingItem[]
  clickSource?: IClickSource,
  isGrid?: boolean
}


const ProductListing = (props: ProductListingProps) => {
  const router = useRouter()
  const { pages, initialData } = props;
  const [listingToggle, setListingToggle] = useState<any>();
  const [sortingOptions, setSortingOptions] = useState<ISelectType[]>([] as ISelectType[])
  const {selectedSortingOptionCtx, setSelectedSortingOptionCtx} = useContext(ListingSortingContext)
  const messagesQuery = useMessages()
  const topProducts = props.pages && props.pages[0].products.slice(0, 4);
  const compareItemsIds: any = topProducts?.map((product: any) =>  product.productID).join("-")
  const countryLang = useCountryLang()

  useEffect(() => {
    // check if there's a listing type preference in localStorage, set default if not
    if (getLocalStorageItem("listingType") && !props.isGrid) {
      setListingToggle(getLocalStorageItem("listingType"))
    } else if (props.isGrid) {
      setListingToggle(ListingType.GRID)
    } else {
      setListingToggle(ListingType.LIST)
    }

    // populate sort options array + reformat
    const sortOptions: ISelectType[] = [];
    if (pages) {
      pages[0].sortOptions && pages[0].sortOptions.map(item => {
        const option: ISelectType = {
          value: item.id,
          label: item.dir ? item.text + ` (${item.dir})` : item.text
        }
        sortOptions.push(option)
      })
      setSortingOptions(sortOptions)

      // check if there's a preferred sorting in url, or fallback to default
      if (router.query.sort) {
        const sort = sortOptions.filter(option => option.value == router.query.sort)
        setSelectedSortingOptionCtx(sort[0])
      }
      else {
        // Set default sort option context
        setSelectedSortingOptionCtx(sortOptions[0])
      }
    }
  }, [])


  const handleListingToggle = (mode: ListingType) => {
    setLocalStorageItem("listingType", mode);
    setListingToggle(mode)
  }


  const getColorForIcon = (target: ListingType) => {
    return listingToggle === target ? baseTheme.colors.primary : baseTheme.colors.secondaryFont;
  }


  const isGrid = () => {
    return listingToggle === ListingType.GRID;
  }


  const handleSortingChange = async (option: ISelectType | null) => {
    await setSelectedSortingOptionCtx(option);
    // persist to url
    if (option) {
      await router.push({
        query: {
          ...(router.query),
          ...(option.label && {sort: option.value})
        }
      }, undefined, {shallow: true})
    }
  }


  const renderItems = () => (
    <>
      {initialData != null && initialData.map((product: IProductListingItem) => (
        <Col key={product.productID} xl={isGrid() ? 3 : 12} lg={isGrid() ? 4 : 12} md={isGrid() ? 6 : 12}>
          <ProductCard product={product} key={product.productID} clickSource={props.clickSource} />
        </Col>
      ))}

      {pages != null && pages.map((group: any, i: number) => (
        <React.Fragment key={i}>
          {group.products.map((product: IProductListingItem) => (
            <Col className="grid-col" key={product.productID} xl={isGrid() ? 3 : 12} lg={isGrid() ? 4 : 12} md={isGrid() ? 6 : 12}>
              <ProductCard variant={listingToggle} product={product} key={product.productID} clickSource={props.clickSource} />
            </Col>
          ))}
        </React.Fragment>
      ))}

      {props.products != null && props.products.map((product, i: number) => (
        <React.Fragment key={i}>
          <Col className="grid-col" key={product.productID} xl={isGrid() ? 3 : 12} lg={isGrid() ? 4 : 12} md={isGrid() ? 6 : 12}>
            <ProductCard variant={listingToggle} product={product} key={product.productID} clickSource={props.clickSource} />
          </Col>
        </React.Fragment>
      ))}
    </>
  )

  const compareTopProductsLink = (
    <div className="compare-top-prods">
      {messagesQuery.data &&
        <Link href={`/${countryLang}/comparison/${compareItemsIds}`}>
          <div className={"link-content"}>
            {(props.products && props.products.length >= 4) || (props.pages && props.pages[0].totalProductCount >= 4) &&
              <>
                <Icon variant="small"
                      className={"compare-icon"}
                      color={"link"}>list-indefinite</Icon>
                <span>{messagesQuery.data.messages["compare-top-products"].replace("%s", 4)}</span>
              </>
            }
            {(props.products && props.products.length < 4 && props.products.length > 1) ||
              (props.pages && props.pages[0].totalProductCount < 4 && props.pages[0].totalProductCount > 1) &&
              <>
                <Icon variant="small"
                      className={"compare-icon"}
                      color={"link"}>list-indefinite</Icon>
                <span>{messagesQuery.data.messages["compare-top-products"].replace("%s", props.products ?
                  props.products.length : props.pages[0].totalProductCount)}</span>
              </>
            }
          </div>
        </Link>
      }
    </div>
  )

  return (
    <ListingContain>
      <ListingControls>
        <ListingView>
          <Icon variant={"medium"} className="layout-icon" color={getColorForIcon(ListingType.LIST)} onClick={() => handleListingToggle(ListingType.LIST)}>list-unordered</Icon>
          <Icon variant={"medium"} className="layout-icon" color={getColorForIcon(ListingType.GRID)} onClick={() => handleListingToggle(ListingType.GRID)}>layout-grid-line</Icon>
        </ListingView>

        {messagesQuery.data && props.pages &&
          <div className={"top-compare-link"}>
            {compareTopProductsLink}
          </div>
        }

        {(props.pages && props.pages[0].sortOptions) && <SortingOptions>
          <Select options={sortingOptions} isSearchable={false} value={selectedSortingOptionCtx} onChange={handleSortingChange} />
        </SortingOptions>}
      </ListingControls>
        <Row>
          {renderItems()}
        </Row>
    </ListingContain>
  )
}

export default ProductListing;
